import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useGame } from '../context/GameContext';
import EmojiCard from './EmojiCard';
import { Loader2, Clock, Zap } from 'lucide-react';

const GameScreen = () => {
  const { 
    currentRound, 
    score, 
    roundData, 
    isLoading, 
    feedback, 
    handleImageClick,
    timeLeft,
    timeLimit,
    lastBonusPoints,
    gameMode
  } = useGame();
  
  const confettiTriggered = useRef(false);

  // Trigger confetti on correct answer
  useEffect(() => {
    if (feedback?.type === 'correct' && !confettiTriggered.current) {
      confettiTriggered.current = true;
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#FFD23F', '#06D6A0', '#3D5A80', '#EE6C4D']
      });
    }
    if (!feedback) {
      confettiTriggered.current = false;
    }
  }, [feedback]);

  // Calculate grid columns based on number of emojis
  const getGridCols = () => {
    if (!roundData) return 'grid-cols-2';
    const numItems = roundData.images.length;
    if (numItems <= 4) return 'grid-cols-2';
    if (numItems <= 6) return 'grid-cols-3';
    return 'grid-cols-4';
  };

  // Timer progress and color
  const timerProgress = timeLimit > 0 ? (timeLeft / timeLimit) * 100 : 0;
  const timerColor = timerProgress > 50 ? '#06D6A0' : timerProgress > 25 ? '#FFD23F' : '#EE6C4D';
  const isLowTime = timeLeft <= 2;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header with score and timer */}
      <motion.header 
        className="fixed top-0 left-0 right-0 z-50 p-4"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex justify-between items-center max-w-2xl mx-auto">
          <div className="score-display flex items-center gap-2" data-testid="round-display">
            <span className="text-lg">😀</span>
            <span className="font-fredoka text-lg sm:text-xl text-[#293241]">
              Round <span className="text-[#3D5A80] font-bold">{currentRound}</span>
            </span>
          </div>
          
          {/* Timer display */}
          <motion.div 
            className={`score-display flex items-center gap-2 ${isLowTime ? 'animate-pulse' : ''}`}
            data-testid="timer-display"
            style={{ borderColor: timerColor }}
          >
            <Clock size={20} style={{ color: timerColor }} />
            <span 
              className="font-fredoka text-xl tabular-nums"
              style={{ color: timerColor }}
            >
              {timeLeft.toFixed(1)}s
            </span>
          </motion.div>
          
          <div className="score-display" data-testid="score-display">
            <span className="font-fredoka text-lg sm:text-xl text-[#293241]">
              Points: <span className="text-[#EE6C4D] font-bold">{score}</span>
            </span>
          </div>
        </div>

        {/* Timer progress bar */}
        <div className="max-w-2xl mx-auto mt-2">
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <motion.div 
              className="h-full rounded-full transition-colors duration-300"
              style={{ 
                width: `${timerProgress}%`,
                backgroundColor: timerColor
              }}
              initial={{ width: '100%' }}
              animate={{ width: `${timerProgress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
        </div>
      </motion.header>

      {/* Main game area */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 pt-24 pb-4 overflow-hidden">
        {isLoading ? (
          <motion.div
            className="flex flex-col items-center gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Loader2 className="w-12 h-12 animate-spin text-[#3D5A80]" />
            <p className="font-quicksand text-lg text-[#293241]/70">
              Finding emojis...
            </p>
          </motion.div>
        ) : roundData ? (
          <div className="w-full max-w-2xl h-full flex flex-col">
            {/* Instruction */}
            <motion.p 
              className="text-center font-quicksand text-sm sm:text-base text-[#293241]/80 mb-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              key={currentRound}
            >
              Find the emoji that <span className="font-bold text-[#EE6C4D]">doesn't belong</span>
              {!feedback && (
                <span className="text-[#3D5A80] ml-2">
                  - fast = bonus points!
                </span>
              )}
            </motion.p>

            {/* Emoji grid - always rendered, clickable immediately */}
            <div className="relative flex-1 flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.div 
                  key={currentRound}
                  className={`grid ${getGridCols()} gap-3 w-full`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  data-testid="image-grid"
                  style={{ 
                    maxWidth: roundData.images.length <= 4 ? '500px' : roundData.images.length <= 6 ? '600px' : '700px',
                    margin: '0 auto'
                  }}
                >
                  {roundData.images.map((emoji, index) => (
                    <EmojiCard
                      key={`${currentRound}-${index}`}
                      emoji={emoji}
                      index={index}
                      onClick={handleImageClick}
                      feedback={feedback}
                      disabled={feedback !== null || timeLeft <= 0}
                      showCategory={feedback?.type === 'wrong' || feedback?.type === 'timeout' || feedback?.type === 'correct'}
                      category={index === roundData.correct_index ? roundData.odd_category : roundData.category}
                      isCorrectAnswer={index === roundData.correct_index}
                    />
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Bonus points overlay - fixed center of screen */}
            <AnimatePresence>
              {feedback?.type === 'correct' && lastBonusPoints > 0 && (
                <motion.div
                  className="fixed inset-0 flex items-center justify-center pointer-events-none z-50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20, scale: 0.5 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.5 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    data-testid="bonus-points-badge"
                  >
                    <span className="inline-flex items-center gap-2 bg-[#FFD23F] text-[#293241] px-6 py-3 rounded-full font-fredoka text-xl shadow-lg">
                      <Zap size={24} className="text-[#EE6C4D]" />
                      +{lastBonusPoints} bonus!
                    </span>
                  </motion.div>
                </motion.div>
              )}
              {feedback?.type === 'timeout' && (
                <motion.div
                  className="fixed inset-0 flex items-center justify-center pointer-events-none z-50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    data-testid="timeout-message"
                  >
                    <span className="inline-flex items-center gap-2 bg-[#EE6C4D] text-white px-6 py-3 rounded-full font-fredoka text-xl shadow-lg">
                      <Clock size={24} />
                      Time's up!
                    </span>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Info */}
            <motion.div
              className="mt-3 text-center flex justify-center gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <span className="text-xs text-[#293241]/50 font-quicksand">
                {roundData.images.length} emojis
              </span>
              <span className="text-xs text-[#293241]/50 font-quicksand">
                {timeLimit.toFixed(1)}s time limit
              </span>
            </motion.div>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default GameScreen;
